import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/home/HeroSection.css";

const HeroSection = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      type: "Food Donation",
      title: "Feed The Hungry",
      subtitle: "Share Your Blessings",
      description:
        "Every meal you donate brings hope to a family in need. Join us in fighting hunger.",
      image:
        "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1920&q=80",
      color: "#165C5C",
      gradient:
        "linear-gradient(135deg, rgba(22, 92, 92, 0.95), rgba(34, 139, 139, 0.85))",
      icon: "🍽️",
      stats: { number: "50K+", label: "Meals Served" },
    },
    {
      id: 2,
      type: "Medicine Support",
      title: "Healing Through Care",
      subtitle: "Save Lives With Medicine",
      description:
        "Your medicine donations help patients access life-saving treatments and medications.",
      image:
        "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=1920&q=80",
      color: "#FFB800",
      gradient:
        "linear-gradient(135deg, rgba(22, 92, 92, 0.95), rgba(34, 139, 139, 0.85))",
      icon: "💊",
      stats: { number: "30K+", label: "Patients Helped" },
    },
    {
      id: 3,
      type: "Blood Donation",
      title: "Be A Life Saver",
      subtitle: "Donate Blood, Save Lives",
      description:
        "Every blood donation can save up to three lives. Your contribution matters immensely.",
      image:
        "https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=1920&q=80",
      color: "#165C5C",
      gradient:
        "linear-gradient(135deg, rgba(22, 92, 92, 0.95), rgba(34, 139, 139, 0.85))",
      icon: "🩸",
      stats: { number: "15K+", label: "Lives Saved" },
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const handleSlideChange = (index) => {
    setCurrentSlide(index);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section className="hs-hero-wrapper">
      {/* Background Slider */}
      <div className="hs-hero-bg">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`hs-hero-slide ${index === currentSlide ? "hs-active" : ""}`}
            style={{
              backgroundImage: `${slide.gradient}, url(${slide.image})`,
            }}
          >
            <div className="hs-hero-overlay"></div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <Container className="hs-hero-container">
        <Row className="align-items-center hs-hero-row">
          <Col lg={7} md={12} className="hs-hero-text-col">
            <div className="hs-hero-content">
              {/* Category Badge */}
              <div className="hs-category-badge hs-animate-slide-up">
                <span className="hs-category-icon">{currentSlideData.icon}</span>
                <span className="hs-category-text">{currentSlideData.type}</span>
              </div>

              {/* Main Title */}
              <h1 className="hs-main-title hs-animate-fade-in">

                Smart donation management system

              </h1>

              {/* Slide Title */}
              <h2 className="hs-slide-title hs-animate-slide-right">
                {currentSlideData.title}
              </h2>

              {/* Subtitle */}
              <h3 className="hs-subtitle hs-animate-slide-right hs-delay-1">
                {currentSlideData.subtitle}
              </h3>

              {/* Tagline */}
              <p className="hs-tagline hs-animate-fade-in hs-delay-2">
                "Donate Easily. Help Quickly. Track Transparently."
              </p>

              {/* Description */}
              <p className="hs-description hs-animate-fade-in hs-delay-3">
                {currentSlideData.description}
              </p>

              {/* Action Buttons - All 3 in one line */}
              <div className="hs-hero-btns hs-animate-fade-in hs-delay-4">
                <button
                  className="hs-btn hs-btn-primary"
                  onClick={() => navigate("/donor")}
                >
                  <span className="hs-btn-icon">❤️</span>
                  <span>Donate Now</span>
                </button>
                <button
                  className="hs-btn hs-btn-secondary"
                  onClick={() => navigate("/receiver")}
                >
                  <span className="hs-btn-icon">🤝</span>
                  <span>Request Help</span>
                </button>
                <button
                  className="hs-btn hs-btn-tertiary"
                  onClick={() => navigate("/join-ngo")}
                >
                  <span className="hs-btn-icon">🏢</span>
                  <span>Join as NGO</span>
                </button>
              </div>
            </div>
          </Col>

          {/* Right Side - Feature Cards */}
          <Col lg={5} md={12} className="hs-hero-cards-col">
            <div className="hs-floating-cards">
              {/* Main Featured Card */}
              <div className="hs-featured-card hs-animate-float">
                <div
                  className="hs-card-icon-wrap"
                  style={{ backgroundColor: currentSlideData.color }}
                >
                  <span className="hs-card-big-icon">{currentSlideData.icon}</span>
                </div>
                <h4 className="hs-card-title">{currentSlideData.type}</h4>
                <p className="hs-card-text">
                  Making a difference in lives every day
                </p>
                <div className="hs-card-progress">
                  <div className="hs-progress-bar">
                    <div
                      className="hs-progress-fill"
                      style={{
                        width: "75%",
                        backgroundColor: currentSlideData.color,
                      }}
                    ></div>
                  </div>
                  <span className="hs-progress-label">75% Goal Achieved</span>
                </div>
              </div>

              {/* Small Info Cards */}
              <div className="hs-info-cards">
                <div className="hs-info-card hs-animate-float hs-delay-1">
                  <div className="hs-info-icon">🎯</div>
                  <h5>Verified</h5>
                  <p>100% Transparent</p>
                </div>
                <div className="hs-info-card hs-animate-float hs-delay-2">
                  <div className="hs-info-icon">⚡</div>
                  <h5>Fast</h5>
                  <p>Quick Processing</p>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Slider Dots */}
        {/* <div className="hs-slide-dots">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              className={`hs-dot ${index === currentSlide ? "hs-dot-active" : ""}`}
              onClick={() => handleSlideChange(index)}
            >
              <span className="hs-dot-icon">{slide.icon}</span>
              <span className="hs-dot-label">{slide.type}</span>
            </button>
          ))}
        </div> */}
      </Container>

      {/* Decorative Elements */}
      <div className="hs-hero-decorations">
        <div className="hs-deco-circle hs-circle-1"></div>
        <div className="hs-deco-circle hs-circle-2"></div>
        <div className="hs-deco-circle hs-circle-3"></div>
      </div>
    </section>
  );
};

export default HeroSection;